import React, { useCallback, useMemo, useState } from 'react';

import { stopEventPropagation } from '@lyearn/core/utils/events';
import { useTranslation } from '@lyearn/i18n';
import { Plus } from '@lyearn/icons';
import { Autocomplete, Stack, Typography } from '@lyearn/ui';
import useDebounceState from '@/hooks/useDebounceState';
import WideCardLoader from '@/modules/content/components/ContentCard/Wide/Loader';
import { useLearningContentsQuery } from '@/modules/corporate/graphql/queries/generated/contents';
import { RenderInput } from '@/modules/corporate/modules/course/components/QuizCardContainer/components/Search/renderers';
import { QuizCardContainerProps } from '@/modules/corporate/modules/course/components/QuizCardContainer/types';
import { useCourse } from '@/modules/corporate/modules/course/courseContext';
import { AutocompleteRenderOptionState } from '@/modules/corporate/modules/database/fields/components/DatabaseSelect/components/OptionItem/types';
import { ResourceStatusEnum, ResourceTypeEnum, SurveyContentType } from '@/types/schema';

import { useDuplicateSurveyMutation } from '../../../../graphql/mutation/generated/duplicateSurvey';

interface ISurveyOption {
  type: 'survey';
  id: string;
  name: string;
}

interface ICreateOption {
  type: 'create';
}

type IOption = ISurveyOption | ICreateOption;

const CREATE_OPTION: ICreateOption = { type: 'create' };

const getOptionLabel = (option: IOption) => (option.type === 'create' ? '' : option.name);

const isOptionEqualToValue = (option: IOption, value: IOption) => {
  if (option.type === 'create' || value.type === 'create') {
    return false;
  }
  return option.id === value.id;
};

type Props = Pick<QuizCardContainerProps, 'updateAttribute' | 'id'> & {
  onCreateNew: () => void;
};

function CourseAttachSurvey(props: Props) {
  const { t } = useTranslation();
  const { updateAttribute, id, onCreateNew } = props;
  const [inputValue, setValue, debouncedValue] = useDebounceState('');
  const [selectedValue, setSelectedValue] = useState<IOption | undefined>(undefined);

  const { course } = useCourse();

  const [{ data }] = useLearningContentsQuery({
    variables: {
      filters: {
        types: [ResourceTypeEnum.Survey],
        name: debouncedValue,
        status: [ResourceStatusEnum.Published, ResourceStatusEnum.UnpublishedChanges],
        shareable: course.isSent ? true : undefined,
      },
    },
  });

  const [{ fetching }, duplicateSurvey] = useDuplicateSurveyMutation();

  const onChange = useCallback(
    async (_event: React.ChangeEvent<{}>, value: IOption | null) => {
      if (!value) {
        return;
      }
      if (value.type === 'create') {
        onCreateNew();
        setSelectedValue(undefined);
        return;
      }
      setSelectedValue(value);
      const res = await duplicateSurvey({
        input: { surveyId: value.id, parentContentId: id },
      });
      if (res.error) {
        throw new Error(res.error.message);
      } else if (res.data) {
        updateAttribute({ contentId: res.data.duplicateSurvey.survey!.id });
      }
    },
    [duplicateSurvey, id, onCreateNew, updateAttribute],
  );

  const onInputChange = useCallback(
    (_event: React.ChangeEvent<{}>, value: string) => {
      setValue(value);
    },
    [setValue],
  );

  const options: IOption[] = useMemo(() => {
    const surveyOptions: ISurveyOption[] = data?.contents.edges?.length
      ? data.contents.edges.map(({ node }) => {
          const { id, name } = node as SurveyContentType;
          return { type: 'survey' as const, id, name };
        })
      : [];
    return [CREATE_OPTION, ...surveyOptions];
  }, [data?.contents.edges]);

  // Server-side filtering via debouncedValue handles survey search.
  // Pass all options through so the create option is always visible.
  const filterOptions = useCallback((opts: IOption[]) => opts, []);

  const renderInput = useCallback(
    (params: any) => (
      <RenderInput
        params={params}
        onBlurPlaceholder={t('Course:Builder.AttachSurvey')}
        onFocusPlaceholder={t('Course:Builder.SearchSurvey')}
      />
    ),
    [t],
  );

  const optionLabelRenderer = useCallback(
    (option: IOption, state: AutocompleteRenderOptionState) => {
      if (option.type === 'create') {
        return (
          <Stack alignItems="center" className="max-w-full" gapX={4}>
            <Plus className="h-16 w-16 flex-shrink-0 fill-current text-text-placeholder" />
            <Typography color="text-text-placeholder" variant="body-short-01">
              {t('Course:Builder.CreateSurvey')}
            </Typography>
          </Stack>
        );
      }
      return (
        <Typography
          className="truncate"
          color={state.selected ? 'text-text-primary' : 'text-text-secondary'}
          variant="body-short-01">
          {option.name}
        </Typography>
      );
    },
    [t],
  );

  if (fetching) {
    return <WideCardLoader className="py-60" />;
  }

  return (
    <Autocomplete
      disableClearable
      disablePortal
      filterOptions={filterOptions}
      getOptionLabel={getOptionLabel}
      inputValue={inputValue}
      isOptionEqualToValue={isOptionEqualToValue}
      optionLabelRenderer={optionLabelRenderer}
      options={options}
      popupIcon={null}
      renderInput={renderInput}
      size="large"
      value={selectedValue}
      onChange={onChange}
      onInputChange={onInputChange}
      onKeyDown={stopEventPropagation}
      onKeyPress={stopEventPropagation}
      onPaste={stopEventPropagation}
    />
  );
}

export default CourseAttachSurvey;
